import React, { useMemo } from 'react';
import { Theme } from '@app/theme';
import {
  ApplicationAnalytics,
  Session,
  useForm,
  useRecruitment,
} from '@app/api';
import {
  LabelList,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import { createUseStyles, useTheme } from 'react-jss';
import { StatsCard } from './StatsCard';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    ...theme.fonts.label,
  },
  label: {
    ...theme.fonts.caption,
  },
}));

interface Props {
  analytics: ApplicationAnalytics[];
  session: Session;
}
export const FieldAnswersCount = ({ analytics, session }: Props) => {
  const classes = useStyles();
  const theme = useTheme<Theme>();
  const { t } = useTranslation();

  const { data: recruitment, isLoading: recruitmentIsLoading } = useRecruitment(
    session.recruitmentId,
  );
  const formId = recruitment ? recruitment.screeningFormId : undefined;
  const { data: form } = useForm(formId);

  const fieldsById = useMemo(() => {
    if (!form?.fields) {
      return {};
    }
    return form?.fields?.reduce((acc, field) => {
      acc[field.id] = field;
      return acc;
    }, {} as Record<string, any>);
  }, [form]);

  const data = useMemo(() => {
    if (!form?.fields) {
      return [];
    }
    const byFieldId: Record<string, number> = {};
    for (const track of analytics) {
      if (!track.fieldId) {
        continue;
      }
      if (!byFieldId[track.fieldId]) {
        byFieldId[track.fieldId] = 0;
      }
      byFieldId[track.fieldId] += track.count;
    }

    return form?.fields?.map((field) => ({
      fieldId: field.id,
      total: byFieldId[field.id] || 0,
      title: field.title || field.id,
    }));
  }, [analytics, fieldsById]);

  if (!session.recruitmentId || (recruitment && !recruitment.screeningFormId)) {
    return null;
  }

  return (
    <StatsCard title={t('sessions.details.stats.fieldAnswersCount')}>
      <ResponsiveContainer height={500} className={classes.container}>
        <BarChart
          width={800}
          height={500}
          layout="vertical"
          data={data}
          margin={{
            top: theme.marginBase * 3,
            right: theme.marginBase * 3,
            bottom: theme.marginBase * 3,
            left: theme.marginBase * 3,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis tick={theme.fonts.caption} type="number" />
          <YAxis
            tick={theme.fonts.caption}
            type="category"
            dataKey="title"
            width={theme.marginBase * 25}
          />
          <Tooltip />
          <Bar dataKey="total" fill={theme.colors.green}>
            <LabelList
              className={classes.label}
              dataKey="total"
              position="right"
              fill={theme.colors.inkSecondary}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </StatsCard>
  );
};
